import { sortByKey } from '@daily/shared/lib/sortByKey';
import { DailyEventObjectParticipant } from '@daily-co/daily-js';
import {
  useAppMessage,
  useDaily,
  useLocalSessionId,
  useThrottledDailyEvent,
} from '@daily-co/daily-react';
import deepEqual from 'fast-deep-equal';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  atom,
  selectorFamily,
  useRecoilCallback,
  useRecoilValue,
} from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import { MAIN_ROOM_IDENTIFIER } from '/components/BreakoutRooms/helpers/constants';
import { useCallConfig } from '/hooks/useCallConfig';
import { useDebugging } from '/hooks/useDebugging';
import { SkinToneModifier, useEmojiSkinTone } from '/hooks/useEmojiSkinTone';
import { useSidebarView } from '/lib/state/core';

import { hasReactedBefore } from '../util/advancedChat';
import { BROADCAST_CHAT_MESSAGE, DEBUG_GIF_MESSAGES } from '../util/constants';

// The base emoji we allow for reactions to chat messages. Emoji that allow skin tone modifiers
// are also included.
export const defaultReactions = [
  '👏',
  '👍',
  '👎',
  '❤️',
  '😂',
  '🤯',
  '🎉',
  '👋',
  '➕',
  '🔥',
];

// The initial reactions that are part of the chat message. We'll fill these arrays with the
// session_ids of the people who reacted with that specific emoji.
const initialReactions: Record<Reaction, any> = {
  '❤️': [],
  '🎉': [],
  '👍': [],
  '👎': [],
  '👏': [],
  '👋': [],
  '😂': [],
  '🤯': [],
  '➕': [],
  '🔥': [],
};

export type Reaction = typeof defaultReactions[number];
type SkinToneReaction = `${'👍' | '👏' | '👎' | '👋'}${SkinToneModifier}`;
export type SkinToneReactions = Partial<Record<Reaction, string[]>>;

export interface ChatMessage {
  date: Date;
  fromId: string;
  id: string;
  local: boolean;
  message: string;
  name?: string;
  reactions?: Record<Reaction, SkinToneReactions>;
  received?: Date;
  room?: string;
  seen?: boolean;
}
interface ChatMessageData {
  event: 'chat-msg';
  date: Date;
  message: string;
  name: string;
  room: string;
}
interface DebugChatConfig {
  gifRatio?: number;
  interval?: number;
  localRatio?: boolean;
  messageCount?: number;
  participantCount?: number;
  startDate?: Date;
}
interface RequestHistoryData {
  event: 'request-chat-history';
}
interface SyncChatMessageData {
  event: 'sync-chat-msg';
  message: ChatMessage;
}
interface SyncChatCompleteData {
  event: 'sync-chat-complete';
}
interface MessageReactionData {
  event: 'message-reaction';
  message: ChatMessage;
  reaction: Reaction;
  skinTone: SkinToneReaction;
}
type AppMessageData =
  | ChatMessageData
  | RequestHistoryData
  | SyncChatMessageData
  | SyncChatCompleteData
  | MessageReactionData;

interface ContextValue {
  loading: boolean;
  reactToMessage(message: ChatMessage, reaction: Reaction): void;
  sendMessage(m: string, r?: string): void;
}

const SYNC_CAP = 1000;
const SYNC_MAX_WAITING_TIME = 60 * 1000;
const SYNC_MAX_WAITING_TIME_OPEN = 5 * 1000;

export const ChatContext = createContext<ContextValue>(null);

const isEqualMessage = (m1: ChatMessage, m2: ChatMessage) =>
  m1.fromId === m2.fromId &&
  m1.message === m2.message &&
  Math.abs(m1.date.getTime() - m2.date.getTime()) < 300;

export const chatMessagesState = atom<ChatMessage[]>({
  key: 'chat-messages',
  default: [],
});

export const chatUnreadState = selectorFamily({
  key: 'chat-unread',
  get:
    (room: string) =>
    ({ get }) => {
      const messages = get(chatMessagesState).filter((m) =>
        [BROADCAST_CHAT_MESSAGE, room].includes(m.room)
      );
      return messages.some((m) => !m.seen);
    },
});

export const ChatProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const daily = useDaily();
  const { enableAdvancedChat, enableChat } = useCallConfig();
  const [sidebarView] = useSidebarView();
  const { prebuiltDebuggerEnabled } = useDebugging();
  const localSessionId = useLocalSessionId();
  const { addModifier, removeModifier } = useEmojiSkinTone();

  const toggleReaction = useCallback(
    (
      chatMessage: ChatMessage,
      reaction: Reaction,
      modifiedEmoji: SkinToneReaction,
      sessionId = localSessionId
    ): SkinToneReactions => {
      const reactions: SkinToneReactions =
        chatMessage?.reactions?.[reaction] ?? {};

      const reactionsCopy = { ...reactions };

      if (hasReactedBefore(reactionsCopy, sessionId)) {
        // this person has already left the same (type of) emoji so remove it
        Object.keys(reactionsCopy).forEach((skinTone) => {
          reactionsCopy[skinTone] = reactionsCopy[skinTone].filter(
            (id) => id !== sessionId
          );
        });
      } else {
        // this person has not left the same emoji reaction before, so add it
        reactionsCopy[modifiedEmoji] = [
          ...(reactionsCopy[modifiedEmoji] ?? []),
          sessionId,
        ];
      }

      return reactionsCopy;
    },
    [localSessionId]
  );

  const [waitingForSyncToComplete, setWaitingForSyncToComplete] =
    useState(true);
  const waitingForSync = useRef<boolean>(true);
  const requestedPeers = useRef<Record<string, number>>({});

  useThrottledDailyEvent(
    'participant-updated',
    useRecoilCallback(
      ({ set }) =>
        (evts: DailyEventObjectParticipant[]) => {
          set(chatMessagesState, (n) => {
            let newMessages = n.slice();
            evts.forEach(({ participant }) => {
              newMessages = newMessages.map((msg) => {
                if (msg.fromId === participant.session_id) {
                  return {
                    ...msg,
                    name: participant.user_name,
                  };
                }
                return msg;
              });
            });
            if (deepEqual(n, newMessages)) return n;
            return newMessages;
          });
        },
      []
    )
  );

  const addMessage = useRecoilCallback(
    ({ set }) =>
      (m: ChatMessage) => {
        set(chatMessagesState, (old) => {
          if (old.some((oldM) => isEqualMessage(oldM, m))) {
            return old;
          }
          return [...old, m].sort(sortByKey('received'));
        });
      },
    []
  );

  const sendAppMessage = useAppMessage<AppMessageData>({
    onAppMessage: useRecoilCallback(
      ({ set, snapshot }) =>
        async (ev, sendAppMessage) => {
          if (!enableChat) return;
          switch (ev?.data?.event) {
            case 'chat-msg':
              addMessage({
                date: new Date(ev.data?.date),
                fromId: ev.fromId,
                id: uuidv4(),
                local: false,
                message: ev.data?.message,
                name: ev.data?.name,
                received: new Date(),
                reactions: enableAdvancedChat
                  ? {
                      ...initialReactions,
                    }
                  : null,
                room: ev.data.room,
                seen: sidebarView === 'chat',
              });
              break;
            case 'request-chat-history':
              if (waitingForSyncToComplete) {
                /**
                 * This peer is awaiting chat history sync themselves, so nothing to respond with.
                 */
                break;
              }
              const messages = await snapshot.getPromise(chatMessagesState);
              [...messages]
                // Send SYNC_CAP messages at max. Older messages won't be synced to avoid potential network bottlenecks.
                .slice(-SYNC_CAP)
                .forEach((message) => {
                  sendAppMessage(
                    {
                      event: 'sync-chat-msg',
                      message,
                    },
                    ev.fromId
                  );
                });
              sendAppMessage(
                {
                  event: 'sync-chat-complete',
                },
                ev.fromId
              );
              break;
            case 'sync-chat-msg': {
              const newMessage: ChatMessage = {
                ...ev.data?.message,
                date: new Date(ev.data?.message?.date),
                id: uuidv4(),
                local: false,
                received: new Date(ev.data?.message?.received),
                seen: sidebarView === 'chat',
              };
              addMessage(newMessage);
              break;
            }
            case 'sync-chat-complete':
              setWaitingForSyncToComplete(false);
              // Map not needed anymore at this point
              requestedPeers.current = {};
              break;
            case 'message-reaction':
              if (!enableAdvancedChat) return;
              const { message, reaction, skinTone } = ev.data;
              const messageWithDates: ChatMessage = {
                ...message,
                date: new Date(message.date),
                received: new Date(message.received),
              };
              set(chatMessagesState, (msgs) => {
                return msgs.map((m) => {
                  if (isEqualMessage(messageWithDates, m))
                    return {
                      ...m,
                      reactions: {
                        ...m.reactions,
                        [reaction]: toggleReaction(
                          m,
                          reaction,
                          skinTone,
                          ev.fromId
                        ),
                      },
                    };
                  return m;
                });
              });
              break;
          }
        },
      [
        addMessage,
        enableAdvancedChat,
        enableChat,
        sidebarView,
        toggleReaction,
        waitingForSyncToComplete,
      ]
    ),
  });

  /**
   * Sends a chat message to all other participants via sendAppMessage.
   */
  const sendMessage = useCallback(
    (message: string, room: string) => {
      if (!enableChat) return;
      const date = new Date();
      const local = daily.participants().local;
      addMessage({
        date,
        fromId: local.session_id,
        id: uuidv4(),
        local: true,
        message,
        name: local.user_name,
        reactions: enableAdvancedChat ? { ...initialReactions } : null,
        received: date,
        room,
        seen: true,
      });
      sendAppMessage({
        event: 'chat-msg',
        date,
        message,
        name: local.user_name,
        room,
      });
    },
    [addMessage, daily, enableAdvancedChat, enableChat, sendAppMessage]
  );
  /**
   * Adds or removes reaction to a message.
   */
  const reactToMessage = useRecoilCallback(
    ({ set }) =>
      (msg: ChatMessage, reaction: Reaction) => {
        if (!enableAdvancedChat) return;
        set(chatMessagesState, (msgs) =>
          msgs.map((m) => {
            // get base emoji (the yellow guy)
            // if the reaction has no modifier, that's fine, it'll just return the original emoji
            const baseEmoji = removeModifier(reaction);
            if (isEqualMessage(m, msg)) {
              // if there's a modifier, add it back. if there's no modifier, that's fine too,
              // addModifier() will just return the original yellow guy
              const modifiedEmoji = addModifier(baseEmoji);
              const newMessage: ChatMessage = {
                ...m,
                reactions: {
                  ...m.reactions,
                  [baseEmoji]: toggleReaction(m, baseEmoji, modifiedEmoji),
                },
              };
              sendAppMessage({
                event: 'message-reaction',
                message: m,
                reaction: baseEmoji,
                skinTone: modifiedEmoji,
              });
              return newMessage;
            }
            return m;
          })
        );
      },
    [
      enableAdvancedChat,
      addModifier,
      removeModifier,
      toggleReaction,
      sendAppMessage,
    ]
  );

  /**
   * Requests chat history (via sendAppMessage) from a random remote peer with an older joined_at date.
   * Does not request if no other remote peers of interest are available.
   */
  const requestChatHistory = useCallback(() => {
    const participants = daily.participants();
    if (!participants?.local) return;
    const joinedDate = participants.local.joined_at || 0;
    // Previous in the sense of "those who were in the meeting earlier"
    const previousRemotePeerIds = Object.entries(participants)
      .filter(
        ([id, participant]) =>
          id !== 'local' &&
          participant.joined_at < joinedDate &&
          // don't bother requesting history from anyone we already pinged twice
          (requestedPeers.current[id] ?? 0) < 2
      )
      .map(([id]) => id);
    // Nothing to sync if there are no older remote peers
    if (previousRemotePeerIds.length === 0) {
      setWaitingForSyncToComplete(false);
      return;
    }
    // Pick a random remote peer id and request history
    const randomIdx = Math.floor(previousRemotePeerIds.length * Math.random());
    const randomId = previousRemotePeerIds[randomIdx];
    sendAppMessage(
      {
        event: 'request-chat-history',
      },
      randomId
    );
    requestedPeers.current[randomId] =
      randomId in requestedPeers.current
        ? requestedPeers.current[randomId] + 1
        : 1;
  }, [daily, sendAppMessage]);

  // DEBUGGING: Add configurable fake chat messages
  const debugAddFakeChatMessages = useCallback(
    (config: DebugChatConfig = {}) => {
      if (!enableChat) return;

      const {
        gifRatio = 0.25,
        interval = 1,
        localRatio = 0.25,
        messageCount = 20,
        participantCount = 4,
        startDate = new Date(Date.now() - 5 * 60000),
      } = config;

      const gifList = gifRatio ? DEBUG_GIF_MESSAGES : undefined;
      const localParticipant = daily.participants().local;

      let currentDateSelector = 0;
      let dateList: Date[];

      let currentParticipant: string;
      let participantList: string[];

      // helper function for adding messages to chat
      const addFakeMessage = (
        message: string,
        id: string,
        date = new Date(),
        local: boolean,
        room = MAIN_ROOM_IDENTIFIER
      ) => {
        addMessage({
          date,
          fromId: local ? localParticipant.session_id : currentParticipant,
          id,
          local,
          message,
          name: local ? localParticipant.user_name : currentParticipant,
          reactions: enableAdvancedChat ? { ...initialReactions } : null,
          received: date,
          room,
        });
      };

      console.warn('Generating chat messages...');

      // generate participants
      if (participantCount) {
        participantList = [];
        // generate participant ids
        for (let i = 0; i < participantCount; i++) {
          participantList.push(uuidv4());
        }
      }

      // generate dates in range from 'startDate' to now at regular intervals
      if (startDate) {
        dateList = [];
        const dateDelta = Math.abs(Date.now() - startDate.getTime());
        const dateStep = dateDelta / messageCount;
        let tempDate = startDate;
        for (let i = 0; i <= messageCount; i++) {
          dateList.push(tempDate);
          tempDate = new Date(tempDate.getTime() + dateStep);
        }
      }

      // generate chat messages
      if (messageCount) {
        let mCounter = 0;

        // use Interval to regularly add a message in a configurable frequency
        const mInterval = setInterval(() => {
          // clear Interval when all messages are out
          if (mCounter === messageCount) {
            console.warn('Done generating text chat messages.');
            clearInterval(mInterval);
            return;
          }

          // select a random participant
          if (participantCount) {
            currentParticipant =
              participantList[
                Math.floor(Math.random() * participantList.length)
              ];
          }

          const messageId = uuidv4();
          const isLocal = !!localRatio && Math.random() <= localRatio;

          if (!!gifRatio && Math.random() <= gifRatio) {
            // generate gif message
            addFakeMessage(
              gifList[Math.floor(Math.random() * gifList.length)],
              messageId,
              startDate && dateList[currentDateSelector],
              isLocal
            );
          } else {
            // generate text message
            addFakeMessage(
              `Test message #${messageId}`,
              messageId,
              startDate && dateList[currentDateSelector],
              isLocal
            );
          }

          mCounter++;
          if (startDate) currentDateSelector++;
        }, interval);
      }
    },
    [addMessage, daily, enableAdvancedChat, enableChat]
  );

  useEffect(() => {
    window['dailyDebugAddFakeChatMessages'] = prebuiltDebuggerEnabled
      ? debugAddFakeChatMessages
      : undefined;
  }, [debugAddFakeChatMessages, prebuiltDebuggerEnabled]);

  /**
   * Starts requesting chat history when joined-meeting.
   */
  useEffect(() => {
    if (
      !daily ||
      daily.isDestroyed() ||
      !enableChat ||
      !waitingForSyncToComplete
    )
      return;
    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;

    // Randomize delay to increase the chance of lowering overall network traffic
    const requestDelay = 1000 + Math.ceil(2000 * Math.random());

    const handleJoinedMeeting = () => {
      timeout = setTimeout(() => {
        // No chat history within ${SYNC_MAX_WAITING_TIME}. Abort!
        waitingForSync.current = false;
        setWaitingForSyncToComplete(false);
      }, SYNC_MAX_WAITING_TIME);
      interval = setInterval(() => {
        if (!waitingForSync.current) {
          clearInterval(interval);
          return;
        }
        requestChatHistory();
      }, requestDelay);
    };
    daily.on('joined-meeting', handleJoinedMeeting);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      daily.off('joined-meeting', handleJoinedMeeting);
    };
  }, [daily, enableChat, requestChatHistory, waitingForSyncToComplete]);

  /**
   * When opening chat while syncing, wait at most 5 additional seconds.
   */
  useEffect(() => {
    if (sidebarView !== 'chat') return;
    const timeout = setTimeout(() => {
      if (waitingForSync.current) {
        waitingForSync.current = false;
        setWaitingForSyncToComplete(false);
      }
    }, SYNC_MAX_WAITING_TIME_OPEN);
    return () => {
      clearTimeout(timeout);
    };
  }, [sidebarView, waitingForSyncToComplete]);

  return (
    <ChatContext.Provider
      value={{
        loading: waitingForSyncToComplete,
        reactToMessage,
        sendMessage,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatState = () => useContext(ChatContext);

export const useChatMessages = () => {
  const messages = useRecoilValue(chatMessagesState);
  const markMessagesAsRead = useRecoilCallback(
    ({ set }) =>
      () => {
        set(chatMessagesState, (msgs) =>
          msgs.some((m) => !m.seen)
            ? msgs.map((m) => ({ ...m, seen: true }))
            : msgs
        );
      },
    []
  );
  return {
    messages,
    markMessagesAsRead,
  };
};
export const useChatUnread = (room: string) =>
  useRecoilValue(chatUnreadState(room));
